/**
 * Pack-size tier selector (1 / 2 / 3 units).
 *
 * Owns the "how many" state of the PDP buy box and publishes it two ways:
 *
 * - `data-pack-count` / `data-pack-percent` on the section root, so
 *   components that connect later (the purchase card, the bundle builder)
 *   can read the current tier without having seen the event, and CSS can
 *   key off the count (e.g. hide the bundle toggle for a single pack).
 * - A bubbling `pdp:pack-change` CustomEvent with `{ count, percent }`.
 *
 * It also writes the count into Horizon's (visually hidden) quantity
 * stepper, which is what the product form submits in one-flavor mode.
 *
 * The tier is the one source of truth and this component owns it: the
 * radios carry `autocomplete="off"` so the browser never restores them on
 * its own, the chosen count is kept in sessionStorage per product, and on
 * connect and on every `pageshow` (Back from the checkout, a bfcache
 * restore) the radio is set from that record and everything above is
 * published again. A click on the already-checked radio re-applies too.
 *
 * Stock: `data-stock` carries how many of the selected variant can still
 * be sold (blank when overselling is allowed or inventory isn't tracked).
 * In one-flavor mode a tier the stock can't cover is disabled and labelled,
 * and the selection drops to the largest tier it covers, so the cart never
 * accepts fewer packs than the page sold. In Mix & Match mode the tier is
 * spread across flavors, so the gate lifts and the flavor chips carry it.
 *
 * Lookups are live queries and the change listener is delegated on the host
 * element, so the markup can be replaced without rebinding.
 */
import { StandardEvents } from '@shopify/events';

const STORAGE_PREFIX = 'tiiga-pdp-pack:';

class PdpPackSizeComponent extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** 'single' until the bundle builder says otherwise */
  #flavorMode = 'single';

  /** The tier the shopper picked before stock dropped the selection, if any */
  #wantedCount = 0;

  connectedCallback() {
    // A fresh controller per connection: a node a morph moves is
    // disconnected and connected again, and an aborted signal adds nothing
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener('change', this.#apply, { signal });
    // A click on the checked radio fires no change event; re-apply anyway so
    // a tap always leaves the price and quantity agreeing with the radio
    this.addEventListener('click', this.#handleClick, { signal });
    // A featured-product section is morphed whole after a variant change: the
    // option classes come back at the server default and other components
    // re-render from the response, so publish the (preserved) tier again.
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.#section?.addEventListener('flavor-bundle:change', this.#handleBundleChange, { signal });
    window.addEventListener('pageshow', this.#handlePageShow, { signal });

    this.#flavorMode = this.#section?.dataset.flavorMode === 'bundle' ? 'bundle' : 'single';
    this.#restore();
  }

  /**
   * Sets the radio from the one record of the shopper's choice — the URL's
   * `tier` on first use, else the count kept for this product in this
   * session — then publishes. Nothing checked by the browser is trusted.
   */
  #restore = () => {
    const requestedTier = new URLSearchParams(location.search).get('tier');
    const requestedInput = this.#inputs.find((input) => input.value === requestedTier);
    if (requestedInput && !this.hasAttribute('data-tier-restored')) {
      requestedInput.checked = true;
      this.setAttribute('data-tier-restored', '');
    } else {
      const stored = this.#storedCount;
      const storedInput = stored ? this.#inputs.find((input) => Number(input.value) === stored) : undefined;
      if (storedInput) storedInput.checked = true;
    }
    this.#apply();
  };

  /** @param {PageTransitionEvent} event */
  #handlePageShow = (event) => {
    this.#restore();
    // A restored page can still have its controls touched by the browser
    // after this event; publish once more after it has settled
    if (event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
      setTimeout(this.#restore, 300);
    }
  };

  get #storageKey() {
    return `${STORAGE_PREFIX}${this.dataset.productId ?? this.dataset.blockId ?? ''}`;
  }

  /** The count kept for this product in this session, or 0 */
  get #storedCount() {
    try {
      return Number(sessionStorage.getItem(this.#storageKey)) || 0;
    } catch {
      return 0;
    }
  }

  /** @param {number} count */
  #storeCount(count) {
    try {
      sessionStorage.setItem(this.#storageKey, String(count));
    } catch {
      // Storage blocked: the page still works, Back just won't remember
    }
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.type === 'radio' && target.checked) {
      this.#apply();
      return;
    }
    // The stock hint's "Mix & Match" link: the bundle builder (listening at
    // the section) switches mode as this click bubbles on, and its
    // mode broadcast is what lifts the gate (#handleBundleChange). Only the
    // tier the shopper wanted is put back here, so the builder opens with
    // that many slots rather than the one pack stock allowed. Publishing
    // now instead would reach the builder while it is still in single
    // mode, whose echo would re-gate the tier and close the builder.
    if (target instanceof Element && target.closest('[data-ref="stockHint"] [data-mode="bundle"]')) {
      const wanted = this.#inputs.find((input) => Number(input.value) === this.#wantedCount);
      if (wanted) wanted.checked = true;
    }
  };

  /** @param {Event} event */
  #handleBundleChange = (event) => {
    const mode = /** @type {CustomEvent} */ (event).detail?.mode === 'bundle' ? 'bundle' : 'single';
    if (mode === this.#flavorMode) return;
    this.#flavorMode = mode;
    this.#apply();
  };

  /**
   * A flavor change re-renders the section: carry the new variant's stock
   * over from the response, then publish the tier again.
   * @param {Event} event
   */
  #handleProductSelect = (event) => {
    const promise = /** @type {CustomEvent & { promise?: Promise<{ detail?: { html?: Document } }> }} */ (event)
      .promise;
    promise
      ?.then((result) => {
        const html = result?.detail?.html;
        const replacement = html?.querySelector(`pdp-pack-size-component[data-block-id="${this.dataset.blockId}"]`);
        if (replacement instanceof HTMLElement) {
          if (replacement.dataset.stock !== undefined) this.dataset.stock = replacement.dataset.stock;
          else delete this.dataset.stock;
        }
        this.#apply();
      })
      .catch(() => {});
  };

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #section() {
    return this.closest('.product-information') ?? this.closest('.shopify-section');
  }

  get #inputs() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[type="radio"]')]);
  }

  get #checked() {
    return this.#inputs.find((input) => input.checked) ?? this.#inputs[0] ?? null;
  }

  /**
   * Units of the selected variant still sellable, or null when unlimited
   * (overselling allowed, or inventory not tracked).
   */
  get #stock() {
    const raw = this.dataset.stock;
    if (raw === undefined || raw === '') return null;
    const stock = Number(raw);
    return Number.isFinite(stock) ? Math.max(0, stock) : null;
  }

  /**
   * Disables the tiers the stock can't cover and drops the selection to the
   * largest it can. Nothing is gated for a mixed bundle, or once the variant
   * is sold out altogether (the add-to-cart button already says so).
   */
  #applyStock() {
    const stock = this.#stock;
    const gated = stock !== null && stock > 0 && this.#flavorMode === 'single';
    const soldOut = stock === 0 && this.#flavorMode === 'single';
    const hint = this.querySelector('[data-ref="stockHint"]');
    let hintCount = 0;

    for (const input of this.#inputs) {
      const count = Number(input.value) || 1;
      const short = gated && count > stock;
      input.disabled = short;
      const option = input.closest('.pdp-pack__option');
      option?.classList.toggle('pdp-pack__option--unavailable', short || soldOut);
      const badge = option?.querySelector('[data-ref="stock"]');
      if (badge instanceof HTMLElement) {
        badge.hidden = !(short || soldOut);
        badge.textContent = soldOut
          ? (this.dataset.soldOutLabel ?? '')
          : (this.dataset.onlyLeftLabel ?? '').replace('[count]', String(stock));
      }
      if (short && input.checked) {
        input.checked = false;
        hintCount = count;
        this.#wantedCount = count;
      }
    }

    if (gated && !this.#inputs.some((input) => input.checked)) {
      const fallback = [...this.#inputs].reverse().find((input) => !input.disabled);
      if (fallback) fallback.checked = true;
    }

    if (hint instanceof HTMLElement) {
      // Offer Mix & Match only where there is a builder to switch to
      const canMix = Boolean(this.#section?.querySelector('flavor-bundle-component'));
      const show = gated && hintCount > stock && Boolean(this.dataset.stockHintLabel);
      hint.hidden = !show;
      if (show) {
        const text = hint.querySelector('[data-ref="stockHintText"]');
        if (text) {
          text.textContent = (this.dataset.stockHintLabel ?? '')
            .replace('[count]', String(stock))
            .replace('[wanted]', String(hintCount));
        }
        const mix = hint.querySelector('[data-mode="bundle"]');
        if (mix instanceof HTMLElement) mix.hidden = !canMix;
      }
    }
  }

  #apply = () => {
    this.#applyStock();

    const checked = this.#checked;
    const count = Number(checked?.value) || 1;
    const percent = Number(checked?.dataset.percent) || 0;
    const url = new URL(location.href);
    if (url.searchParams.has('tier')) {
      url.searchParams.set('tier', String(count));
      history.replaceState(history.state, '', url);
    }

    for (const input of this.#inputs) {
      input.closest('.pdp-pack__option')?.classList.toggle('pdp-pack__option--selected', input === checked);
    }

    this.#storeCount(count);

    const section = this.#section;
    if (!(section instanceof HTMLElement)) return;

    section.dataset.packCount = String(count);
    section.dataset.packPercent = String(percent);
    section.dispatchEvent(new CustomEvent('pdp:pack-change', { bubbles: true, detail: { count, percent } }));

    this.#syncQuantity(section, count);
  };

  /**
   * @param {HTMLElement} section
   * @param {number} count
   */
  async #syncQuantity(section, count) {
    await customElements.whenDefined('quantity-selector-component');
    /** @type {any} */
    const quantity = section.querySelector('.product-details quantity-selector-component');
    quantity?.setValue?.(count);
  }
}

if (!customElements.get('pdp-pack-size-component')) {
  customElements.define('pdp-pack-size-component', PdpPackSizeComponent);
}
